import React from "react"
import { Simulation } from "./simulation"
import { useInterval } from "../hooks/interval.hook"

const mod = (n: number, m: number) => ((n % m) + m) % m

export type SimulationData = {
  dots: Array<{
    speed: number
    mass: number
    name: string
    isAlive: boolean
    x: number
    y: number
  }>
  selectedIndex: number
}

const SimulationDataContext = React.createContext<{
  readSimulation: () => SimulationData
  handleAmountChange: (amount: number) => void
  handleSpeedChange: (speed: number) => void
  nextPlanet: () => void
  previousPlanet: () => void
}>({
  readSimulation: () => ({ dots: [], selectedIndex: 0 }),
  handleAmountChange: () => undefined,
  handleSpeedChange: () => undefined,
  nextPlanet: () => undefined,
  previousPlanet: () => undefined,
})

export const SimulationDate = ({
  getDate,
}: {
  getDate: (simulationData: SimulationData) => string
}) => {
  const date = useSimulationDataSelector(getDate)
  return React.useMemo(() => <>{date}</>, [date])
}

export const SimulationDataProvider = ({
  simulation,
  children,
}: React.PropsWithChildren<{ simulation: Simulation }>) => {
  const simulationRef = React.useRef(simulation)

  const readSimulation = React.useCallback(
    (): SimulationData => ({
      dots: simulationRef.current.dots.map(dot => ({
        speed: dot.speed.magnitude(),
        mass: dot.mass,
        name: dot.name,
        isAlive: dot.isAlive,
        x: dot.position.x,
        y: dot.position.y,
      })),
      selectedIndex: simulation.selectedDot
        ? simulation.dots.indexOf(simulation.selectedDot)
        : 0,
    }),
    [simulation.dots, simulation.selectedDot]
  )

  const handleAmountChange = (amount: number) => {
    simulation.initializeDots(amount)
  }
  const handleSpeedChange = (speed: number) => {
    simulation.speed = speed
  }
  const nextPlanet = () => {
    if (simulation.selectedDot) {
      const index = simulation.livingDots.indexOf(simulation.selectedDot)
      simulation.selectDot(
        simulation.livingDots[mod(index + 1, simulation.livingDots.length)]
      )
    }
  }
  const previousPlanet = () => {
    if (simulation.selectedDot) {
      const index = simulation.livingDots.indexOf(simulation.selectedDot)
      simulation.selectDot(
        simulation.livingDots[mod(index - 1, simulation.livingDots.length)]
      )
    }
  }

  return (
    <SimulationDataContext.Provider
      value={{
        readSimulation,
        handleAmountChange,
        handleSpeedChange,
        nextPlanet,
        previousPlanet,
      }}
    >
      {children}
    </SimulationDataContext.Provider>
  )
}

export const useSimulationData = () => React.useContext(SimulationDataContext)

export function useSimulationDataSelector<T>(
  selector: (simulationData: SimulationData) => T
) {
  const simulationContext = React.useContext(SimulationDataContext)
  const [data, setData] = React.useState<T>(
    selector(simulationContext.readSimulation())
  )

  React.useEffect(() => {
    const newData = selector(simulationContext.readSimulation())
    if (newData !== data) {
      setData(newData)
    }
  }, [data, selector, simulationContext])

  useInterval(() => {
    const newData = selector(simulationContext.readSimulation())
    if (newData !== data) {
      setData(newData)
    }
  }, 500)

  return React.useMemo(() => data, [data])
}
